/**
 * PUBLIC app configuration for the mobile app.
 *
 * This module is the ONLY sanctioned source of environment configuration in the
 * mobile bundle. It reads the generated `.generated/public-config.json` snapshot
 * (emitted by `scripts/generate-public-config.ts` from environments/<env>/public.yaml).
 *
 * SECURITY: the mobile app must NEVER import `@opncue/server-config` or
 * `@opncue/build-config`, and there is deliberately no code path here that could
 * reach a secret. Everything below is public by construction — the generator
 * refuses to emit secret-like keys.
 */

export interface MobilePublicConfig {
  environment: "dev" | "stg" | "prod";
  apiBaseUrl: string;
  socketUrl: string;
  webBaseUrl: string;
  useMock: boolean;
  app: {
    name: string;
    scheme: string;
    oauthRedirectScheme: string;
    oauthRedirectPath: string;
  };
  stripe: { publishableKey: string };
  /**
   * RevenueCat PUBLIC SDK keys (appl_… / goog_…), one per store platform.
   * Public like Stripe's publishableKey. Empty = native in-app purchases are
   * off and the RevenueCat provider reports "unavailable".
   */
  revenuecat: { iosPublicSdkKey: string; androidPublicSdkKey: string };
  /**
   * Client crash reporting — two Sentry projects behind one bundle.
   *
   * Native and web share this source tree but report separately
   * (opncue-mobile, opncue-web), so both DSNs ship and the SDK picks one at
   * init. Use {@link sentryDsnForPlatform} rather than reading these directly.
   *
   * Empty means off, which is the state everywhere today — `lib/telemetry.ts`
   * installs no client and exports nothing until one carries a value.
   */
  sentry: {
    /** The switch. False means nothing is sent, whatever the DSN says. */
    enabled: boolean;
    /** Performance tracing, 0..1. Errors are unaffected; 0 = crashes only. */
    tracesSampleRate: number;
    mobile: { dsn: string };
    web: { dsn: string };
  };
  oauth: {
    googleWebClientId: string;
    googleIosClientId: string;
    googleAndroidClientId: string;
    appleBundleIds: string[];
    appleWebClientId: string;
    appleWebRedirectUri: string;
  };
  verification: { codeExpiryMinutes: number; resendCooldownSeconds: number };
  features: {
    conversationPractice: boolean;
    nearbyDiscovery: boolean;
    voicePractice: boolean;
  };
  locales: { supported: string[]; default: string };
  location: {
    /** Slider anchors for the people-discovery radius: min ("Close by"),
     * centre ("Nearby", also the default), max ("Local area"). Display hints
     * only — the server clamps against its own bounds. */
    nearbyUserCloseByRadiusMeters: number;
    nearbyUserRadiusMeters: number;
    nearbyUserLocalAreaRadiusMeters: number;
    /** How far out venue search may show places. */
    venueSearchRadiusMeters: number;
    /**
     * How ACCURATE the fix must be to check in anywhere. Not the distance
     * bound — see below. Server is authoritative for both.
     */
    venueJoinRadiusMeters: number;
    /** How close the user must be to check in. The "move closer" bound. */
    venueJoinDistanceMeters: number;
  };
}

function loadGenerated(): MobilePublicConfig {
  try {
    // Static string require so Metro bundles the JSON. The file is git-ignored
    // and produced by `npm run config:generate` (run automatically before start).
    return require("../generated/public-config.json") as MobilePublicConfig;
  } catch {
    throw new Error(
      "Missing generated public config. Run `npm run config:generate` " +
        "(APP_ENV=dev by default) to create src/generated/public-config.json before bundling.",
    );
  }
}

export const publicConfig: MobilePublicConfig = loadGenerated();

export const PUBLIC_ENV = publicConfig.environment;

export function appSchemeUrl(path: string): string {
  const cleanPath = path.replace(/^\/+/, "");
  return `${publicConfig.app.scheme}://${cleanPath}`;
}

export function nativeOAuthRedirectUri(
  scheme: string,
  path: string,
): string {
  const cleanPath = path.replace(/^\/+/, "");
  return `${scheme}:/${cleanPath}`;
}

export const oauthRedirectUri =
  nativeOAuthRedirectUri(
    publicConfig.app.oauthRedirectScheme,
    publicConfig.app.oauthRedirectPath,
  );

/** Everything an SDK needs to decide whether, where, and as what to report. */
export type SentryClientConfig = {
  /** True only when reporting is switched on AND an address exists. */
  enabled: boolean;
  dsn: string;
  /** Which environment a report belongs to — dev, stg or prod. */
  environment: MobilePublicConfig["environment"];
  tracesSampleRate: number;
  /**
   * The build a report belongs to, or `null` to let the SDK decide.
   *
   * **Web supplies one; native must not.** `nativeReleaseIntegration` is a
   * DEFAULT integration of the React Native reporting SDK (named in the adapter,
   * not here — see `lib/telemetry-sentry.ts`), and it reads
   * `<bundleId>@<version>+<build>` from the native build — the same value
   * `sentry-cli` stamps on the source maps it uploads during that build. Its own
   * source says an `options.release` is "stronger than the release/dist from the
   * native build", so passing one here would override the value the maps were
   * uploaded under and guarantee they never match. On web there is no native
   * build to read, so the field stays empty unless we set it, and an event with
   * no release can never be matched to a map either.
   */
  release: string | null;
  /** Distinguishes builds sharing a release. Same rule as `release`. */
  dist: string | null;
};

/**
 * The web build's identity, stamped at export time.
 *
 * `EXPO_PUBLIC_*` is inlined by `expo export` when the bundle is built (Expo
 * SDK 56 environment-variables guide), which is the only moment a commit is
 * knowable — the same reason the API takes `OPNCUE_RELEASE` as a build arg.
 *
 * Shaped like the API's `opncue-api@1.0.0+c9c7d1d` so one convention covers
 * every surface, and an unstamped build reads `unknown` rather than guessing —
 * a release that silently lies about which commit it is would be worse than one
 * that admits it does not know.
 */
const WEB_RELEASE = process.env.EXPO_PUBLIC_RELEASE ?? "";

/**
 * Whether this platform reports, where to, and under which environment.
 *
 * Native and web are one bundle and two Sentry projects (opncue-mobile,
 * opncue-web), so the choice has to be made somewhere. Making it here means it
 * is made once, with one rule, rather than at every place a client is
 * initialised — which is how one surface ends up reporting into the other's
 * project, or into production from a developer's laptop.
 *
 * **`enabled` is an AND, not an OR.** The switch and the address are separate
 * settings and both must be present: a DSN with the switch off must not report
 * (that is the point of the switch), and the switch on with no DSN must not
 * try (that is a crash inside the crash reporter).
 *
 * `environment` is what separates dev, staging and production inside a single
 * project — one DSN per project, three environments within it — so it is read
 * from the same config rather than set at each call site.
 *
 * Takes the platform rather than reading `Platform.OS` so this module keeps
 * importing nothing, which is what lets `tests/config/` load it outside a
 * bundler.
 */
export function sentryConfigForPlatform(
  platform: "web" | "native",
): SentryClientConfig {
  const dsn =
    platform === "web"
      ? publicConfig.sentry.web.dsn
      : publicConfig.sentry.mobile.dsn;

  return {
    enabled: publicConfig.sentry.enabled && dsn.length > 0,
    dsn,
    environment: publicConfig.environment,
    tracesSampleRate: publicConfig.sentry.tracesSampleRate,
    // Native deliberately supplies neither — see the type. An unstamped web
    // build supplies neither either, rather than inventing a release that
    // nothing was uploaded under.
    release: platform === "web" && WEB_RELEASE.length > 0 ? WEB_RELEASE : null,
    dist: platform === "web" && WEB_RELEASE.length > 0 ? WEB_RELEASE : null,
  };
}
